/**
 * ClassifierDetails — structured «критерии выбора → цена» list. Moved out of
 * ServiceManagementPage.tsx (KAN-157 god-component split).
 *
 * Renders `service.pricing_data` (tariff items) instead of the raw LLM
 * prompt_block. Each item is robust to shape:
 *   { type:'tariff', work | name, variant?, price, unit? }
 * Rows are grouped by the work/name so a single work with many variants reads
 * as one block of «вариант → цена» rows.
 */
import type { PricingItem, Service } from '@/services/api/services';
import { CalcEmptyNotice } from './CalcEmptyNotice';

// Format a classifier price value with a thousands separator + unit, so the
// reader instantly sees «при каких условиях какая цена». Robust to string or
// numeric `price`.
function formatPrice(price: number | string | undefined | null, unit: string | undefined): string {
  if (price === undefined || price === null || price === '') return '—';
  const num = typeof price === 'number' ? price : Number(price);
  const formatted = Number.isFinite(num) ? num.toLocaleString('ru-RU') : String(price);
  return unit ? `${formatted} ${unit}` : formatted;
}

export function ClassifierDetails({ service }: { service: Service }): JSX.Element {
  const items: PricingItem[] = Array.isArray(service.pricing_data) ? service.pricing_data : [];

  if (items.length === 0) {
    return <CalcEmptyNotice testid="classifier-empty" message="Нет вариантов" />;
  }

  // Group by work/name, preserving first-seen order. Items without a work
  // label fall into a single «—» group so nothing is dropped.
  const groups: { work: string; items: PricingItem[] }[] = [];
  const indexByWork = new Map<string, number>();
  items.forEach(item => {
    const work = (item.work ?? item.name ?? '—').trim() || '—';
    let groupIdx = indexByWork.get(work);
    if (groupIdx === undefined) {
      groupIdx = groups.length;
      indexByWork.set(work, groupIdx);
      groups.push({ work, items: [] });
    }
    groups[groupIdx].items.push(item);
  });

  return (
    <div data-testid="classifier-variants" className="space-y-4">
      {groups.map((group, gi) => (
        <div key={`${group.work}-${gi}`} className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-800">{group.work}</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4 font-medium">Условия выбора</th>
                  <th className="py-2 font-medium text-right">Цена</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item, idx) => {
                  const condition = item.variant && item.variant.trim() ? item.variant : 'Базовый тариф';
                  return (
                    <tr key={idx} data-testid="classifier-variant-row" className="border-b last:border-b-0">
                      <td className="py-2 pr-4 text-gray-800">{condition}</td>
                      <td className="py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                        {formatPrice(item.price, item.unit)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
